import { useAction, useAtom } from '@reatom/npm-react';
import { Product } from '@trwl/backend.api';
import { AIRPLANE_DELIVERY_TAX } from '@trwl/constants';
import { Path } from '@trwl/shared/lib/router';
import { DeliveryModeEnum } from '@trwl/shared/types';
import { Button } from '@trwl/shared/ui/button';
import { Payment } from '@trwl/shared/ui/icons';
import { addToCartAction, cartAtom, setFastCartAction } from '@trwl/store';
import { $variant } from '@trwl/store/product';
import { InfoTooltip } from '@trwl/widgets/product/infoTooltip';
import { DeliveryModeSelector } from '@trwl/widgets/product/productInfo/components/DeliveryModeSelector';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import { useRouter } from 'next/navigation';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

export const AddToCart = ({ product }: { product: Product }) => {
  const deliveryState = useState<DeliveryModeEnum>(DeliveryModeEnum.DEFAULT);
  const [deliveryMode] = deliveryState;
  const [cart] = useAtom(cartAtom);
  const addToCart = useAction(addToCartAction);
  const setFastCart = useAction(setFastCartAction);
  const router = useRouter();

  const [variant] = useUnit([$variant]);

  const inCart = useMemo(() => {
    if (!variant) return false;
    return cart.some(
      (item) =>
        item.id === variant.id &&
        (deliveryMode === DeliveryModeEnum.AIRPLANE) === item.airplaneDelivery,
    );
  }, [cart, variant, deliveryMode]);

  const images = useMemo(
    () => product.images.map((image) => image.url),
    [product],
  );

  const slug = useMemo(() => product.id, [product]);
  const isAirplane = deliveryMode === DeliveryModeEnum.AIRPLANE;

  return (
    <>
      {variant && (
        <>
          <div className="flex flex-row w-full items-center gap-[15px] mt-[25px]">
            <p className="text-[22px] text-[#282828]">
              Выберите способ доставки
            </p>

            <InfoTooltip>
              <div className={'font-light leading-[22px]'}>
                В редких случаях цена может быть изменена,
                <br />
                уведомим вас об этом по почте
              </div>
            </InfoTooltip>
          </div>
          <DeliveryModeSelector variant={variant} state={deliveryState} />
          <div className="flex flex-row w-[100%] justify-center items-center gap-[15px] my-0.5">
            <p className="text-[14px] text-[#ABABAB] font-light text-center">
              *Доставка СДЭКом. Срок указан для <br />
              Москвы, в другие города немного дольше
            </p>
          </div>
        </>
      )}

      <div className="w-full flex flex-row lg:flex-col gap-[10px] mt-[15px]">
        <Button
          fullWidth
          size="lg"
          radius="sm"
          color="primary"
          disabled={!variant}
          startContent={<Payment width={29} height={29} />}
          className={'gap-2'}
          onPress={() => {
            if (!variant) return toast.error(`Что-то пошло не так!`);

            setFastCart({
              id: variant.id,
              airplaneDelivery: isAirplane,
              name: product.title,
              properties: variant.saleProperties.map((property) => ({
                key: property.propertyName,
                value: property.propertyValue,
              })),
              image: images[0],
              url: `${Path.PRODUCT}/${slug}?variantId=${variant.id}`,
              price: variant.price + (isAirplane ? AIRPLANE_DELIVERY_TAX : 0),
              minDelivery: variant.minDelivery,
              maxDelivery: variant.maxDelivery,
            });

            router.push(`${Path.ORDER}?fast=true`);
          }}
        >
          Заказать
        </Button>
        {!isAirplane && (
          <Button
            fullWidth
            variant={inCart ? 'solid' : 'bordered'}
            color="primary"
            size="lg"
            radius="sm"
            disabled={!variant}
            className={cn(!inCart && 'border-[1.5px]')}
            onPress={() => {
              if (inCart) return router.push('/cart');
              if (!variant) return toast.error(`Что-то пошло не так!`);

              addToCart({
                id: variant.id,
                airplaneDelivery: isAirplane,
                name: product.title,
                properties: variant.saleProperties.map((property) => ({
                  key: property.propertyName,
                  value: property.propertyValue,
                })),
                image: images[0],
                url: `${Path.PRODUCT}/${slug}?variantId=${variant.id}`,
                price: variant.price + (isAirplane ? AIRPLANE_DELIVERY_TAX : 0),
                minDelivery: variant.minDelivery,
                maxDelivery: variant.maxDelivery,
              });

              toast.success(
                `Продукт ${product.title} успешно добавлен в корзину!`,
              );
            }}
          >
            <div className="flex flex-col gap-[5px]">
              {variant
                ? inCart
                  ? 'Перейти в корзину'
                  : 'Добавить в корзину'
                : 'Вариант не найден'}
            </div>
          </Button>
        )}
      </div>
    </>
  );
};
